/* ==========================================================================
   GHOST ASCENT — motion layer
   Scroll reveal, header behaviour, drop countdown, sticky mobile ATC,
   enter gate, sold-out badges.

   Design rules this file follows:
   - Everything degrades. If this script dies, the site is a normal dark store.
   - Nothing here caches or alters cart/product DATA. It is presentation only.
   - prefers-reduced-motion is honoured, not ignored.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Mark that JS is alive. ga-brand.css uses this to decide whether the
     reveal animation is safe to hide content behind. */
  document.documentElement.classList.add('ga-js');

  /* ------------------------------------------------------------------ *
   * 1. SCROLL REVEAL
   * Any element with .ga-reveal fades up once, when it enters view.
   * Children of [data-ga-stagger] get an incremental delay.
   * ------------------------------------------------------------------ */
  function initReveal() {
    var targets = document.querySelectorAll('.ga-reveal');
    if (!targets.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('ga-in'); });
      return;
    }

    document.querySelectorAll('[data-ga-stagger]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-ga-stagger'), 10) || 90;
      group.querySelectorAll('.ga-reveal').forEach(function (el, i) {
        el.style.setProperty('--ga-delay', (i * step) + 'ms');
      });
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('ga-in');
        io.unobserve(entry.target);          // reveal once, never re-hide
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    targets.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------ *
   * 2. HEADER
   * Adds .ga-scrolled to <body> past the fold, which lifts the sticky white
   * header onto its own plane with a faint shadow (ga-brand.css §5).
   *
   * There is deliberately NO hide-on-scroll. A header that disappears while
   * you are browsing a shop puts the cart one extra gesture away, and on a
   * white site there is nothing to gain visually by removing it.
   * ------------------------------------------------------------------ */
  function initHeader() {
    var ticking = false;

    function update() {
      document.body.classList.toggle('ga-scrolled', window.scrollY > 24);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });

    update();
  }

  /* ------------------------------------------------------------------ *
   * 3. DROP COUNTDOWN  <drop-countdown data-deadline="2026-09-05T18:00:00-05:00">
   *
   * The deadline MUST carry an explicit UTC offset. A bare "2026-09-05T18:00"
   * is parsed in the VISITOR's timezone, so a customer in London would see a
   * different drop time than one in Chicago — for a timed drop that is the
   * whole ballgame. The section renders the offset for you.
   * ------------------------------------------------------------------ */
  function initCountdowns() {
    document.querySelectorAll('[data-ga-countdown]').forEach(function (root) {
      var deadline = Date.parse(root.getAttribute('data-deadline'));
      if (isNaN(deadline)) {
        root.setAttribute('data-state', 'error');
        return;
      }

      var slots = {
        days:    root.querySelector('[data-unit="days"]'),
        hours:   root.querySelector('[data-unit="hours"]'),
        minutes: root.querySelector('[data-unit="minutes"]'),
        seconds: root.querySelector('[data-unit="seconds"]')
      };

      function pad(n) { return n < 10 ? '0' + n : String(n); }

      function tick() {
        var left = deadline - Date.now();

        if (left <= 0) {
          root.setAttribute('data-state', 'live');
          Object.keys(slots).forEach(function (k) {
            if (slots[k]) slots[k].textContent = '00';
          });
          clearInterval(timer);
          /* Let other code (e.g. an "it's live" banner) react. */
          root.dispatchEvent(new CustomEvent('ga:drop-live', { bubbles: true }));
          return;
        }

        var s = Math.floor(left / 1000);
        if (slots.days)    slots.days.textContent    = pad(Math.floor(s / 86400));
        if (slots.hours)   slots.hours.textContent   = pad(Math.floor(s / 3600) % 24);
        if (slots.minutes) slots.minutes.textContent = pad(Math.floor(s / 60) % 60);
        if (slots.seconds) slots.seconds.textContent = pad(s % 60);
        root.setAttribute('data-state', 'counting');
      }

      tick();
      var timer = setInterval(tick, 1000);

      /* Phones throttle timers in a backgrounded tab, so the clock drifts.
         Re-derive from the deadline the moment we come back. */
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) tick();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. STICKY MOBILE ADD-TO-CART
   * Mirrors the real form's submit rather than posting on its own, so
   * variant selection, quantity and any app-injected validation still apply.
   * ------------------------------------------------------------------ */
  function initStickyAtc() {
    var bar = document.querySelector('[data-ga-sticky-atc]');
    if (!bar) return;

    var realForm = document.querySelector('product-form form, form[action*="/cart/add"]');
    var realBtn  = realForm && realForm.querySelector('[type="submit"]');
    var proxy    = bar.querySelector('[data-ga-sticky-submit]');
    if (!realBtn || !proxy) { bar.remove(); return; }

    proxy.addEventListener('click', function (e) {
      e.preventDefault();
      realBtn.click();
    });

    /* Keep the label and disabled state in sync with the real button, so a
       sold-out variant can't be "added" from the sticky bar. */
    function sync() {
      var off = realBtn.disabled || realBtn.getAttribute('aria-disabled') === 'true';
      proxy.disabled = off;
      proxy.textContent = off ? 'Sold out' : 'Add to cart';
    }
    sync();
    new MutationObserver(sync).observe(realBtn, {
      attributes: true, attributeFilter: ['disabled', 'aria-disabled']
    });

    /* Show it only once the original button has scrolled away. */
    if (!('IntersectionObserver' in window)) { bar.classList.add('ga-in'); return; }
    new IntersectionObserver(function (entries) {
      bar.classList.toggle('ga-in', !entries[0].isIntersecting);
    }, { threshold: 0 }).observe(realBtn);
  }

  /* ------------------------------------------------------------------ *
   * 5. ENTER GATE
   * Shows once per browser session. sessionStorage, not localStorage —
   * a returning fan should see the splash again on a new visit, but not
   * on every page click within one visit.
   *
   * ⚠ THE GATE FAILS OPEN, AND THAT IS THE MOST IMPORTANT THING ABOUT IT.
   *
   * A full-screen overlay whose only exit is JavaScript is the worst failure
   * mode on the whole site: if this file 404s, or an earlier function throws,
   * or a corporate proxy strips the script, the customer lands on a black
   * screen with no way into the store and no error to explain it. You lose
   * every sale and nothing in Shopify reports a problem.
   *
   * So the CSS hides .ga-gate by DEFAULT. It is revealed only by the
   * .ga-gate--ready class, which is added at the very end of this function —
   * after the dismiss handlers are proven bound. The gate can therefore
   * never appear unless it is already dismissible.
   *
   * If you edit this function, keep the reveal last. Moving it earlier
   * re-creates the trap.
   * ------------------------------------------------------------------ */
  function initGate() {
    var gate = document.querySelector('[data-ga-gate]');
    if (!gate) return;

    /* No exit control in the markup means no gate. Never show it. */
    var buttons = gate.querySelectorAll('[data-ga-gate-enter]');
    if (!buttons.length) { gate.remove(); return; }

    var KEY = 'ga-entered';
    var seen = false;
    try { seen = sessionStorage.getItem(KEY) === '1'; } catch (e) { /* private mode */ }

    /* Skip the gate for anyone arriving on a deep link (a product URL from
       a TikTok bio click) — making them tap through to the thing they
       already asked for is how you lose the sale. */
    var deepLink = window.location.pathname !== '/' && window.location.pathname !== '';

    if (seen || deepLink) { gate.remove(); document.body.classList.remove('ga-locked'); return; }

    function open() {
      try { sessionStorage.setItem(KEY, '1'); } catch (e) {}
      gate.classList.add('ga-gate--out');
      document.body.classList.remove('ga-locked');
      /* Remove after the transition so it can't trap focus. */
      setTimeout(function () { gate.remove(); }, REDUCED ? 0 : 700);
    }

    /* Bind every exit BEFORE the gate is allowed to become visible. */
    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', open);
    });
    gate.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') open();
    });

    /* Only now is it safe to show. See the warning above this function. */
    gate.classList.add('ga-gate--ready');
    document.body.classList.add('ga-locked');
    buttons[0].focus();
  }

  /* ------------------------------------------------------------------ *
   * 6. HERO VIDEO
   * Autoplay is unreliable (low-power mode, data saver). If it refuses,
   * fall back to the poster image rather than showing a dead black box.
   * ------------------------------------------------------------------ */
  function initHeroVideo() {
    document.querySelectorAll('[data-ga-hero-video]').forEach(function (video) {
      if (REDUCED) { video.removeAttribute('autoplay'); video.pause(); return; }
      var attempt = video.play();
      if (attempt && attempt.catch) {
        attempt.catch(function () {
          video.closest('[data-ga-hero]')?.setAttribute('data-video-failed', 'true');
        });
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * BOOT
   * Re-runs on Shopify's section events so everything still works while
   * you are editing in the theme customizer.
   * ------------------------------------------------------------------ */
  /* Each step is isolated. One failing feature must not take the others down
     with it — and in particular must not stop initGate from running, because
     an un-run initGate leaves a customer stranded on the splash screen.
     initGate goes FIRST for the same reason. */
  function run(name, fn) {
    try { fn(); }
    catch (err) { console.error('[ga] ' + name + ' failed:', err); }
  }

  function boot() {
    run('gate', initGate);
    run('reveal', initReveal);
    run('header', initHeader);
    run('countdown', initCountdowns);
    run('sticky-atc', initStickyAtc);
    run('hero-video', initHeroVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', initReveal);
})();
